<template>
	<div class="voucher-edit-form">

		<!-- Form content -->
		<div class="form-content">
			<div class="form-section">
				<label class="form-label">Value you would like to purchase</label>
				<div>
					<InputSections ref="inputSections" 
						:sections="sections" 
						theme="white" 
						:outline="true"
						@input="validateAmount"/>
				</div>
				
				<div class="cashback-section">
					<div class="cashback-display">
						<span class="cashback-text">{{ formatter.format(cashbackAmount) }} cashback</span>
					</div>
				</div>				
				<div class="amount-constraints">
					<p
						class="constraints small"
						:style="errorText ? 'font-weight:bold;color:#d32f2f;' : ''"
					>
						Minimum {{ formatter.format(availableVoucher.lowerAmount) }}.
						Maximum {{ formatter.format(availableVoucher.upperAmount) }}.
						{{ availableVoucher.minorUnit && availableVoucher.minorUnit > 0 ? 'Increments of ' + formatter.format(availableVoucher.minorUnit) : '' }}.
					</p>
				</div>
				<div class="done-button-container">
					<MGButton
						:fullWidth="true"
						text="Update"
						:arrow="false"
						@click="handleDone"
						class="checkout-button"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import InputSections from './InputSections.vue';
import Helpers from '../../mixins/Helpers.js';
import MGButton from './MGButton.vue';

export default {
	name: "VoucherEditForm",
	components: { InputSections, MGButton },
	mixins: [Helpers],
	props: {
		initialAmount: {
			type: Number,
			default: 100
		},
		availableVoucher: {
			type: Object
		},
		formatter: {
			type: Object
		}
	},
	emits: ['close', 'update'],
	data() {
		return {
			amount: this.initialAmount,
			errorText: null,
			sections: [
				{
					fields: [
						{
							id: 'amount',
							type: this.availableVoucher.allowedAmounts ? 'select' : 'currency',
							label: this.availableVoucher.allowedAmounts ? 'Select amount' : 'Enter amount',
							value: this.initialAmount,
							required: true,
							items: this.availableVoucher.allowedAmounts ? this.availableVoucher.allowedAmounts.split(',').map(Number) : [],
							prefix: '£',
							hideClear: true,
						}
					]
				}
			]
		}
	},
	computed: {
		cashbackAmount() {
			if (!this._findFieldValueById('amount')) return 0;
			return (this._findFieldValueById('amount') * this.availableVoucher?.effectiveDiscountPercentage || 0) / 100;
		}
	},
	methods: {
		validateAmount: function() {
			const newAmount = this._findFieldValueById('amount');
			this.errorText = null;
			if (this.amount && this.availableVoucher.upperAmount && parseFloat(newAmount) > this.availableVoucher.upperAmount) {
				this._setFieldValueById('amount', this.availableVoucher.upperAmount);
				this.errorText = "Maximum " + this.formatter.format(this.availableVoucher.upperAmount);
			}
			if (this.amount && this.availableVoucher.lowerAmount && parseFloat(newAmount) < this.availableVoucher.lowerAmount) {
				this.errorText = "Minimum " + this.formatter.format(this.availableVoucher.lowerAmount);
			}
		},
		updateCashback() {
			this.amount = this._findFieldValueById('amount');
			this.$emit('update', {
				amount: parseFloat(this.amount),
				cashback: this.cashbackAmount
			});
		},
		handleDone() {
			if (!this.validAmount()) {
				return;
			}
			this.updateCashback();
			this.$emit('close');
		},
		validAmount: function() {
			this.amount = this._findFieldValueById('amount');
			this.errorText = null;
			
			// Check if amount is empty or invalid
			if (!this.amount || this.amount === '' || isNaN(parseFloat(this.amount))) {
				this.errorText = "Please enter a valid amount";
				return false;
			}
			
			if (this.amount && this.availableVoucher.upperAmount && parseFloat(this.amount) > this.availableVoucher.upperAmount) {
				this.amount = this.availableVoucher.upperAmount;
				this.errorText = "Enter an amount no more than " + this.formatter.format(this.availableVoucher.upperAmount);
				return false;
			}
			if (this.amount && this.availableVoucher.lowerAmount && parseFloat(this.amount) < this.availableVoucher.lowerAmount) {
				this.errorText = "Enter an amount no less than " + this.formatter.format(this.availableVoucher.lowerAmount);
				return false
			}
			return true;
		},
	},
	watch: {
		initialAmount: {
			immediate: true,
			handler(newValue) {
				this.amount = newValue;
			}
		}
	}
}
</script>

<style scoped>
.alert {
	margin-top: -5px;
	margin-bottom: 1px;
}
.done-button-container {
	margin-top: -15px;
}

.voucher-edit-form {
	background: white;
	border-radius: 12px 12px 0 0;
	padding: 24px 24px 40px;
	margin-left: -20px;
	margin-right: -20px;
}

/* Header */
.form-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 24px;
}

.merchant-info {
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1;
	padding-right: 48px;
}

.merchant-logo {
	width: 48px;
	height: 32px;
	border-radius: 4px;
	overflow: hidden;
	background: #f5f5f5;
	box-shadow: 0px 4px 8px 0px rgba(51, 40, 71, 0.05);
}

.merchant-logo img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.merchant-name {
	font-weight: 500;
	font-size: 15px;
	line-height: 1.47em;
	color: #413655;
	text-align: center;
	flex: 1;
}

.close-button {
	position: absolute;
	top: 20px;
	right: 25px;
	width: 30px;
	height: 30px;
	background: #281C3B;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.close-btn {
	color: white !important;
	width: 30px !important;
	height: 30px !important;
}

/* Form Content */
.form-content {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.form-label {
	font-weight: 500;
	font-size: 15px;
	line-height: 1.47em;
	color: #413655;
	display: block;
	margin-bottom: 12px;
}

.placeholder-text {
	font-weight: 400;
	font-size: 11px;
	line-height: 1.45em;
	color: rgba(51, 40, 71, 0.75);
}

.amount-input {
	border: none;
	outline: none;
	background: transparent;
	font-weight: 500;
	font-size: 15px;
	line-height: 1.07em;
	color: #332847;
	width: 100%;
	flex: 1;
}

.amount-input::-webkit-outer-spin-button,
.amount-input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.amount-input[type=number] {
	-moz-appearance: textfield;
	appearance: textfield;
}

.cashback-section {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	margin-bottom: 12px;
	margin-top: -10px;
	width: 100%;
}

.cashback-display {
	background: #FCE5D4;
	border-radius: 8px;
	padding: 8px 12px;
	display: inline-flex;
	align-items: center;
	width: fit-content;
	margin-right: auto;
}

.cashback-text {
	font-weight: bold;
	font-size: 11px;
	line-height: 1.09em;
	color: #413655;
}

.amount-constraints {
	font-size: 13px;
	line-height: 1.23em;
	display: flex;
	flex-direction: column;
	gap: 2px;
}
</style>